/**
 * API client for clinic staff profiles and provider maps (portal.clinic_staff_profile, portal.clinic_staff_provider_map).
 */

import { UserApiClinicianProfileSegment, UserApiRoutePrefix } from '../constants/routeConstants';
import { userClient } from '../services/api.service';

/** Provider map status. Values match API and DB (chk_clinic_staff_provider_map_status). Independent of backend enum. */
export enum ProviderMapStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

/** Filter for approvals list (includes "all"). Query param values for status filter. */
export const ApprovalStatusFilter = {
  All: 'all',
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
} as const;

export type ApprovalStatusFilter = (typeof ApprovalStatusFilter)[keyof typeof ApprovalStatusFilter];

export interface ClinicStaffProfileDto {
  clinicStaffProfileId: number;
  userId: number;
  clinicStaffProfileSpecificDetails?: string | null;
  isActive: boolean;
  createdDate: string;
  createdBy?: string | null;
  updatedDate?: string | null;
  updatedBy?: string | null;
}

export interface ClinicStaffProviderMapDto {
  id: number;
  clinicianProfileId: number;
  clinicStaffProfileId: number;
  status: string;
  isActive: boolean;
  createdDate: string;
  createdBy?: string | null;
  updatedDate?: string | null;
  updatedBy?: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

/** Linked provider for office admin dropdown (GET me/linked-providers). */
export interface LinkedProviderDto {
  clinicianProfileId: number;
  providerDisplayName: string;
  npiNumber?: string | null;
}

/** Applicant summary within an approval item. */
export interface ApplicantSummaryDto {
  clinicStaffProfileId: number;
  userId: number;
  displayName: string;
  email?: string | null;
  roleName?: string | null;
}

/** Provider summary within an approval item. */
export interface ProviderSummaryDto {
  clinicianProfileId: number;
  displayName: string;
  npiNumber?: string | null;
}

/** Single row for the provider approvals grid (pending/approved). */
export interface ProviderApprovalItemDto {
  mapId: number;
  status: string;
  createdDate: string;
  createdBy?: string | null;
  applicant: ApplicantSummaryDto;
  provider: ProviderSummaryDto;
}

/** Paged provider-map approvals for a clinician (GET clinician-profiles/{id}/provider-map-approvals). */
export interface ProviderMapApprovalsPageDto {
  items: ProviderApprovalItemDto[];
  totalCount: number;
  pendingTotalCount: number;
  page: number;
  pageSize: number;
}

/** Pending approvals summary for notification bell (GET me/pending-approvals). */
export interface PendingApprovalsSummaryDto {
  count: number;
  items: ProviderApprovalItemDto[];
}

/** User summary within applicant detail. */
export interface ApplicantUserSummaryDto {
  userId: number;
  firstName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
  displayName: string;
}

/** Combined clinic staff + user for View applicant page. */
export interface ApplicantDetailDto {
  clinicStaffProfileId: number;
  userId: number;
  specificDetails?: string | null;
  isActive: boolean;
  createdDate: string;
  createdBy?: string | null;
  updatedDate?: string | null;
  updatedBy?: string | null;
  user: ApplicantUserSummaryDto;
}

/** Create a clinic staff profile. Returns created profile with clinicStaffProfileId. */
export async function createClinicStaffProfile(body: {
  userId: number;
  clinicStaffProfileSpecificDetails?: string | null;
  isActive?: boolean;
}): Promise<ApiResponse<ClinicStaffProfileDto>> {
  const { data } = await userClient.post<ApiResponse<ClinicStaffProfileDto>>('/api/v1/clinic-staff-profiles', body);
  return data;
}

/** Create a provider map for a clinic staff profile. */
export async function createClinicStaffProviderMap(
  profileId: number,
  body: { clinicianProfileId: number; status?: ProviderMapStatus; isActive?: boolean }
): Promise<ApiResponse<ClinicStaffProviderMapDto>> {
  const { data } = await userClient.post<ApiResponse<ClinicStaffProviderMapDto>>(
    `/api/v1/clinic-staff-profiles/${profileId}/provider-maps`,
    body
  );
  return data;
}

/** Get providers linked to an office admin (for dropdown). userId in path. */
export async function getLinkedProviders(params: { userId: number }): Promise<ApiResponse<LinkedProviderDto[]>> {
  const url = `/api/v1/clinic-staff-profiles/user/${params.userId}/linked-providers`;
  const { data } = await userClient.get<ApiResponse<LinkedProviderDto[]>>(url);
  return data;
}

/** Get pending approvals summary (notification bell). userId in path. */
export async function getMyPendingApprovals(params: {
  userId: number;
  maxItems?: number;
}): Promise<ApiResponse<PendingApprovalsSummaryDto>> {
  const qs = params.maxItems != null ? `?maxItems=${params.maxItems}` : '';
  const url = `/api/v1/clinic-staff-profiles/user/${params.userId}/pending-approvals${qs}`;
  const { data } = await userClient.get<ApiResponse<PendingApprovalsSummaryDto>>(url);
  return data;
}

/** Get provider-map approvals for a clinician by clinicianProfileId (server-side filter, sort, paging). Name/email search is client-side on the current page. */
export async function getProviderMapApprovalsByClinicianProfile(params: {
  clinicianProfileId: number;
  status?: ApprovalStatusFilter;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  /** Omit or 'all' = no filter (matches UI "All Roles"). Otherwise exact applicant role display name. */
  role?: string;
}): Promise<ApiResponse<ProviderMapApprovalsPageDto>> {
  const searchParams = new URLSearchParams();
  if (params.status != null) searchParams.set('status', params.status);
  if (params.sortBy != null && params.sortBy !== '') searchParams.set('sortBy', params.sortBy);
  if (params.sortDir != null) searchParams.set('sortDir', params.sortDir);
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.role != null && params.role !== '' && params.role.toLowerCase() !== 'all') {
    searchParams.set('role', params.role);
  }
  const qs = searchParams.toString();
  const url = `${UserApiRoutePrefix.ClinicianProfiles}/${params.clinicianProfileId}/${UserApiClinicianProfileSegment.ProviderMapApprovals}${qs ? `?${qs}` : ''}`;
  const { data } = await userClient.get<ApiResponse<ProviderMapApprovalsPageDto>>(url);
  return data;
}

/** Approve or reject a provider map. Requires userId as acting approver (identity from UI). Reason required for override (Rejected→Approved) and revoke (Approved→Rejected). */
export async function approveProviderMap(
  mapId: number,
  body: { status: ProviderMapStatus; reason?: string },
  params: { userId: number }
): Promise<ApiResponse<ClinicStaffProviderMapDto>> {
  const searchParams = new URLSearchParams();
  searchParams.set('userId', String(params.userId));
  const url = `/api/v1/clinic-staff-profiles/provider-maps/${mapId}?${searchParams.toString()}`;
  const payload: { status: string; reason?: string } = { status: body.status };
  if (body.reason != null && body.reason !== '') {
    payload.reason = body.reason;
  }
  const { data } = await userClient.patch<ApiResponse<ClinicStaffProviderMapDto>>(url, payload);
  return data;
}

/** Get applicant (clinic staff + user) detail for read-only View applicant page. */
export async function getApplicantDetail(clinicStaffProfileId: number): Promise<ApiResponse<ApplicantDetailDto>> {
  const { data } = await userClient.get<ApiResponse<ApplicantDetailDto>>(
    `/api/v1/clinic-staff-profiles/${clinicStaffProfileId}/applicant-detail`
  );
  return data;
}
